// LLMCostByFunctionTile.js — 30-day cost breakdown by function name.
// Data: llm_usage_daily system docs → aggregated by_function across the window.

import React from 'react';
import { Card, CardContent, Typography, Box, IconButton, Tooltip as MuiTooltip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { useLLMByFunction, toCSV, downloadCSV } from '../../../hooks/useAdminMetrics';

const formatUSD = (n) => `$${n.toFixed(2)}`;

const LLMCostByFunctionTile = ({ days = 30 }) => {
  const { rows, loading, error } = useLLMByFunction(days);

  const handleCSV = () => {
    downloadCSV(
      `llm-cost-by-function-${days}d.csv`,
      toCSV(rows, [
        { key: 'name', header: 'function' },
        { key: 'calls', header: 'calls' },
        { key: 'cost_usd', header: 'cost_usd' },
      ]),
    );
  };

  if (error) {
    return (
      <Card><CardContent>
        <Typography color="error">Cost-by-function unavailable: {error.message}</Typography>
      </CardContent></Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" gutterBottom>LLM Cost by Function ({days}d)</Typography>
          <MuiTooltip title="Download CSV">
            <span>
              <IconButton size="small" onClick={handleCSV} disabled={loading || rows.length === 0}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </span>
          </MuiTooltip>
        </Box>
        <Box sx={{ width: '100%', height: 280 }}>
          {loading ? (
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          ) : rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No data yet.</Typography>
          ) : (
            <ResponsiveContainer>
              <BarChart data={rows} layout="vertical" margin={{ left: 20, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={formatUSD} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => formatUSD(v)} />
                <Bar dataKey="cost_usd" fill="#1976d2" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          LLM cost split by which Maia feature consumed it (decision_engine = phone calls, llm_chat = web chat, etc.).
        </Typography>
      </CardContent>
    </Card>
  );
};

export default LLMCostByFunctionTile;
